/**
 * Admin Clients & Users Page
 *
 * Tabs:
 *   Clients        — searchable list of client organizations (users live under each client)
 *   Unassigned     — client-role users with no organization; assign them in place
 *   Internal team  — CoreSkan staff (admin / operations / sales), who don't belong to a client
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { Client } from '../../../shared/types/auth';
import { EditUserModal, CsvImportModal } from './UsersPage';
import { CreateUserModal } from '../../components/admin/CreateUserModal';
import { ClientImportModal } from '../../components/admin/ClientImportModal';

type Tab = 'clients' | 'unassigned' | 'internal';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'clients', label: 'Clients' },
  { id: 'unassigned', label: 'Unassigned users' },
  { id: 'internal', label: 'Internal team' },
];

const INTERNAL_ROLES = ['admin', 'operations', 'sales'];
const CLIENT_ROLE_LABELS: Record<string, string> = {
  viewer: 'General Viewer',
  commenter: 'User / Commenter',
  people_manager: 'People Manager',
  client_admin: 'Client Admin',
};

function slugify(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function initials(u: any): string {
  return (u.name || u.email || '?').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
}

export function ClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const tab: Tab = tabParam && TABS.some(t => t.id === tabParam) ? tabParam : 'clients';
  const setTab = (t: Tab) => setSearchParams(t === 'clients' ? {} : { tab: t });

  const { user: currentUser } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  // Clients tab state
  const [showClientModal, setShowClientModal] = useState(false);
  const [showClientImport, setShowClientImport] = useState(false);
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // User tab state
  const [editUser, setEditUser] = useState<any | null>(null);
  const [createMode, setCreateMode] = useState<'client' | 'internal' | null>(null);
  const [showCsvImport, setShowCsvImport] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const load = () => {
    Promise.all([api.admin.getClients(), api.admin.getUsers()])
      .then(([c, u]) => { setClients(c); setUsers(u); setSelected(new Set()); })
      .catch((err) => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // ───── Derived lists ─────
  const memberCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const u of users) for (const c of u.clients || []) m.set(c.clientId, (m.get(c.clientId) || 0) + 1);
    return m;
  }, [users]);

  const q = search.trim().toLowerCase();
  const matchUser = (u: any) => !q || [u.name, u.email, u.role].some((v) => v && String(v).toLowerCase().includes(q));

  const filteredClients = useMemo(() => clients.filter((c) => !q || [
    c.name, (c as any).orgCode, c.contactEmail, c.contactName, (c as any).slug,
  ].some((v) => v && String(v).toLowerCase().includes(q))), [clients, q]);

  const unassignedUsers = useMemo(
    () => users.filter((u) => !INTERNAL_ROLES.includes(u.role) && (!u.clients || u.clients.length === 0)).filter(matchUser),
    [users, q],
  );
  const internalUsers = useMemo(
    () => users.filter((u) => INTERNAL_ROLES.includes(u.role)).filter(matchUser),
    [users, q],
  );
  const unassignedTotal = users.filter((u) => !INTERNAL_ROLES.includes(u.role) && (!u.clients || u.clients.length === 0)).length;
  const internalTotal = users.filter((u) => INTERNAL_ROLES.includes(u.role)).length;

  const adminCount = users.filter((u) => u.role === 'admin').length;
  const canDelete = (u: any) => u.id !== currentUser?.id && !(u.role === 'admin' && adminCount <= 1);
  const canChangeRole = (u: any) => !(u.role === 'admin' && adminCount <= 1);

  // ───── Client selection (bulk delete) ─────
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filteredClients.length) setSelected(new Set());
    else setSelected(new Set(filteredClients.map((c) => c.id)));
  };

  if (loading) return <div style={{ color: '#94a3b8' }}>Loading...</div>;

  const tabCount = (t: Tab) => t === 'clients' ? clients.length : t === 'unassigned' ? unassignedTotal : internalTotal;

  return (
    <div>
      {/* Header */}
      <div style={{
        ...styles.header,
        ...(isMobile ? { flexDirection: 'column' as const, alignItems: 'stretch', gap: 12 } : {}),
      }}>
        <h1 style={{ ...styles.heading, ...(isMobile ? { fontSize: 20 } : {}) }}>Clients &amp; Users</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {tab === 'clients' && selected.size > 0 && (
            <button onClick={() => setShowDeleteConfirm(true)} style={{ ...styles.deleteBtn, ...(isMobile ? { minHeight: 44 } : {}) }}>
              Delete ({selected.size})
            </button>
          )}
          {tab === 'clients' && (
            <>
              <button onClick={() => setShowClientImport(true)} style={{ ...styles.secondaryBtn, ...(isMobile ? { minHeight: 44 } : {}) }}>Import CSV</button>
              <button onClick={() => setShowClientModal(true)} style={{ ...styles.newBtn, ...(isMobile ? { minHeight: 44 } : {}) }}>+ New Client</button>
            </>
          )}
          {tab === 'unassigned' && (
            <>
              <button onClick={() => setShowCsvImport(true)} style={{ ...styles.secondaryBtn, ...(isMobile ? { minHeight: 44 } : {}) }}>Import users CSV</button>
              <button onClick={() => setCreateMode('client')} style={{ ...styles.newBtn, ...(isMobile ? { minHeight: 44 } : {}) }}>+ New User</button>
            </>
          )}
          {tab === 'internal' && (
            <button onClick={() => setCreateMode('internal')} style={{ ...styles.newBtn, ...(isMobile ? { minHeight: 44 } : {}) }}>+ New Team Member</button>
          )}
        </div>
      </div>

      {/* Tabs + search */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={styles.tabBar}>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSearch(''); }}
              style={{ ...styles.tabBtn, ...(tab === t.id ? styles.tabBtnActive : {}) }}
            >
              {t.label}
              <span style={{ ...styles.tabCount, ...(t.id === 'unassigned' && unassignedTotal > 0 ? { background: 'rgba(245,158,11,0.2)', color: '#f59e0b' } : {}) }}>
                {tabCount(t.id)}
              </span>
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={tab === 'clients' ? 'Search clients by name, org code, or contact…' : 'Search by name or email…'}
          style={{ ...styles.search, ...(isMobile ? { width: '100%' } : { flex: 1, minWidth: 240 }) }}
        />
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}
      {notice && (
        <div style={styles.noticeBanner}>
          <span>{notice}</span>
          <button onClick={() => setNotice('')} style={{ background: 'none', border: 'none', color: '#00cc88', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
      )}

      {/* ───── Clients tab ───── */}
      {tab === 'clients' && (
        <div style={styles.table}>
          {!isMobile && (
            <div style={{ ...styles.tableHeader, gap: 12 }}>
              <span style={{ width: 32, flexShrink: 0 }}>
                <input type="checkbox" checked={filteredClients.length > 0 && selected.size === filteredClients.length} onChange={toggleAll} style={styles.checkbox} />
              </span>
              <span style={{ width: 44, flexShrink: 0 }}></span>
              <span style={{ flex: 1, minWidth: 0 }}>Name</span>
              <span style={{ width: 80, flexShrink: 0 }}>Org Code</span>
              <span style={{ width: 70, flexShrink: 0, textAlign: 'right' }}>Users</span>
              <span style={{ width: 180, flexShrink: 0 }}>Contact</span>
              <span style={{ width: 100, flexShrink: 0 }}>Enrollment</span>
              <span style={{ width: 100, flexShrink: 0, textAlign: 'right' }}>Created</span>
            </div>
          )}
          {filteredClients.map((c) => {
            const count = memberCounts.get(c.id) || 0;
            const logo = (
              <div style={styles.logoThumb}>
                {c.logoPath ? <img src={c.logoPath} alt="" style={styles.logoThumbImg} /> : (
                  <span style={{ color: '#333', fontSize: 14, fontWeight: 700 }}>{c.name.charAt(0).toUpperCase()}</span>
                )}
              </div>
            );
            return isMobile ? (
              <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, borderBottom: '1px solid #1a1a1a' }}>
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} style={{ ...styles.checkbox, marginTop: 4 }} />
                <Link to={`/admin/clients/${c.id}`} style={{ flex: 1, textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    {logo}
                    <span style={{ flex: 1, fontWeight: 500, color: '#f1f5f9', fontSize: 15 }}>{c.name}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', color: '#64748b', fontSize: 12 }}>
                    <span>Code <span style={{ color: '#6b8a99', fontFamily: 'monospace', fontWeight: 600 }}>{(c as any).orgCode || '—'}</span></span>
                    <span>{count} user{count === 1 ? '' : 's'}</span>
                    <span>{c.contactEmail || '—'}</span>
                    <span>{c.enrollmentEnabled ? <span style={styles.enabledBadge}>Enrollment on</span> : null}</span>
                  </div>
                </Link>
              </div>
            ) : (
              <div key={c.id} style={{ ...styles.row, gap: 12 }}>
                <span style={{ width: 32, flexShrink: 0 }}>
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} style={styles.checkbox} />
                </span>
                <Link to={`/admin/clients/${c.id}`} style={{ display: 'contents', textDecoration: 'none', color: 'inherit' }}>
                  <span style={{ width: 44, flexShrink: 0 }}>{logo}</span>
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 500, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{c.name}</span>
                </Link>
                <span style={{ width: 80, flexShrink: 0, color: '#6b8a99', fontSize: 12, fontFamily: 'monospace', fontWeight: 600, userSelect: 'all', cursor: 'text' }}>{(c as any).orgCode || '—'}</span>
                <Link to={`/admin/clients/${c.id}`} style={{ display: 'contents', textDecoration: 'none', color: 'inherit' }}>
                  <span style={{ width: 70, flexShrink: 0, color: count ? '#94a3b8' : '#475569', fontSize: 13, textAlign: 'right' }}>{count}</span>
                  <span style={{ width: 180, flexShrink: 0, color: '#64748b', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{c.contactEmail || '—'}</span>
                  <span style={{ width: 100, flexShrink: 0 }}>
                    {c.enrollmentEnabled ? <span style={styles.enabledBadge}>Enabled</span> : <span style={styles.disabledBadge}>Off</span>}
                  </span>
                  <span style={{ width: 100, flexShrink: 0, color: '#64748b', fontSize: 13, textAlign: 'right' }}>{new Date(c.createdAt).toLocaleDateString()}</span>
                </Link>
              </div>
            );
          })}
          {filteredClients.length === 0 && (
            <div style={styles.empty}>{q ? 'No clients match your search' : 'No clients yet'}</div>
          )}
        </div>
      )}

      {/* ───── Unassigned users tab ───── */}
      {tab === 'unassigned' && (
        <>
          <p style={styles.hint}>
            Client users who are not a member of any organization. Assign each one to the right client here, or open the
            client and add them from its Members section.
            {' '}<Link to="/admin/users" style={{ color: '#4F7CFF' }}>All users (advanced)</Link>
          </p>
          <div style={styles.table}>
            {unassignedUsers.map((u) => (
              <UnassignedRow
                key={u.id}
                user={u}
                clients={clients}
                isMobile={isMobile}
                onAssigned={load}
                onEdit={() => setEditUser(u)}
                onError={setError}
              />
            ))}
            {unassignedUsers.length === 0 && (
              <div style={{ ...styles.empty, color: q ? '#64748b' : '#00cc88' }}>
                {q ? 'No unassigned users match your search' : 'Every client user is assigned to an organization.'}
              </div>
            )}
          </div>
        </>
      )}

      {/* ───── Internal team tab ───── */}
      {tab === 'internal' && (
        <>
          <p style={styles.hint}>
            CoreSkan staff. Admins and Operations can use this console; Sales has view-only access. Staff do not belong to a client.
          </p>
          <div style={styles.table}>
            {!isMobile && (
              <div style={{ ...styles.tableHeader, gap: 12 }}>
                <span style={{ width: 36, flexShrink: 0 }}></span>
                <span style={{ flex: 1 }}>Name</span>
                <span style={{ flex: 1 }}>Email</span>
                <span style={{ width: 110 }}>Role</span>
                <span style={{ width: 90 }}>Status</span>
                <span style={{ width: 100, textAlign: 'right' }}>Created</span>
                <span style={{ width: 70 }}></span>
              </div>
            )}
            {internalUsers.map((u) => (
              <div key={u.id} style={{ ...styles.row, gap: 12, ...(isMobile ? { flexWrap: 'wrap' as const } : {}) }}>
                <span style={{ width: 36, flexShrink: 0 }}><Avatar user={u} /></span>
                <span style={{ flex: 1, minWidth: 120, color: '#f1f5f9', fontWeight: 500 }}>{u.name || '—'}</span>
                <span style={{ flex: 1, minWidth: 160, color: '#94a3b8', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</span>
                <span style={{ width: 110 }}><RoleBadge role={u.role} /></span>
                <span style={{ width: 90, color: u.status === 'active' ? '#94a3b8' : '#f59e0b', fontSize: 12 }}>{u.status || 'active'}</span>
                {!isMobile && <span style={{ width: 100, color: '#64748b', fontSize: 13, textAlign: 'right' }}>{new Date(u.createdAt).toLocaleDateString()}</span>}
                <span style={{ width: 70, textAlign: 'right' }}>
                  <button onClick={() => setEditUser(u)} style={styles.smallBtn}>Edit</button>
                </span>
              </div>
            ))}
            {internalUsers.length === 0 && <div style={styles.empty}>{q ? 'No team members match your search' : 'No internal team members yet'}</div>}
          </div>
        </>
      )}

      {/* ───── Modals ───── */}
      {showClientModal && (
        <CreateClientModal isMobile={isMobile} onClose={() => setShowClientModal(false)} onCreated={() => { setShowClientModal(false); load(); }} />
      )}
      {showDeleteConfirm && (
        <BulkDeleteConfirmModal ids={Array.from(selected)} isMobile={isMobile} onClose={() => setShowDeleteConfirm(false)} onDeleted={() => { setShowDeleteConfirm(false); load(); }} />
      )}
      {showClientImport && (
        <ClientImportModal
          onClose={() => setShowClientImport(false)}
          onImported={(created) => { setNotice(`Imported ${created} client${created === 1 ? '' : 's'}.`); load(); }}
        />
      )}
      {createMode && (
        <CreateUserModal mode={createMode} clients={clients} onClose={() => setCreateMode(null)} onCreated={load} />
      )}
      {showCsvImport && (
        <CsvImportModal clients={clients} onClose={() => setShowCsvImport(false)} onImported={() => { setShowCsvImport(false); load(); }} />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          clients={clients}
          canChangeRole={canChangeRole(editUser)}
          canDelete={canDelete(editUser)}
          onClose={() => setEditUser(null)}
          onSaved={load}
          onDeleted={load}
        />
      )}
    </div>
  );
}

// ─── Small pieces ────────────────────────────────────────────────────────────

function Avatar({ user }: { user: any }) {
  return (
    <div style={{
      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
      background: user.profilePhotoPath ? `url(${user.profilePhotoPath}) center/cover` : 'rgba(79,124,255,0.15)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#4F7CFF', fontSize: 11, fontWeight: 700, border: '1px solid rgba(255,255,255,0.08)',
    }}>
      {!user.profilePhotoPath && initials(user)}
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const color = role === 'admin' ? '#f59e0b' : role === 'sales' ? '#22c55e' : role === 'operations' ? '#a78bfa' : '#4F7CFF';
  return <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color }}>{role}</span>;
}

function UnassignedRow({ user, clients, isMobile, onAssigned, onEdit, onError }: {
  user: any; clients: Client[]; isMobile: boolean; onAssigned: () => void; onEdit: () => void; onError: (m: string) => void;
}) {
  const [clientId, setClientId] = useState('');
  const [role, setRole] = useState('viewer');
  const [busy, setBusy] = useState(false);

  const assign = async () => {
    if (!clientId) return;
    setBusy(true);
    onError('');
    try {
      await api.admin.addClientMember(clientId, user.id, role);
      onAssigned();
    } catch (err: any) {
      onError(err.message || 'Failed to assign user');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ ...styles.row, gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
      <span style={{ width: 36, flexShrink: 0 }}><Avatar user={user} /></span>
      <span style={{ flex: 1, minWidth: 160 }}>
        <div style={{ color: '#f1f5f9', fontWeight: 500 }}>{user.name || '—'}</div>
        <div style={{ color: '#94a3b8', fontSize: 12 }}>{user.email}</div>
      </span>
      <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', ...(isMobile ? { width: '100%' } : {}) }}>
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ ...styles.select, minWidth: 180 }} disabled={busy}>
          <option value="">Assign to organization…</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={role} onChange={(e) => setRole(e.target.value)} style={{ ...styles.select, minWidth: 150 }} disabled={busy || !clientId}>
          {Object.entries(CLIENT_ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={assign} disabled={!clientId || busy} style={{ ...styles.approveBtn, opacity: clientId ? 1 : 0.4 }}>
          {busy ? 'Assigning…' : 'Assign'}
        </button>
        <button onClick={onEdit} style={styles.smallBtn}>Edit</button>
      </span>
    </div>
  );
}

function BulkDeleteConfirmModal({ ids, isMobile, onClose, onDeleted }: { ids: string[]; isMobile: boolean; onClose: () => void; onDeleted: () => void }) {
  const [info, setInfo] = useState<Array<{ id: string; name: string; userCount: number; projectCount: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.admin.bulkClientInfo(ids)
      .then(setInfo)
      .catch((err) => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const totalUsers = info.reduce((sum, c) => sum + c.userCount, 0);
  const totalProjects = info.reduce((sum, c) => sum + c.projectCount, 0);
  const confirmRequired = `DELETE ${info.length}`;
  const canDelete = confirmText === confirmRequired;

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.admin.bulkDeleteClients(ids);
      onDeleted();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
      setDeleting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.modal, width: 520 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={{ ...styles.modalTitle, color: '#ef4444' }}>Delete {ids.length} Client{ids.length > 1 ? 's' : ''}</h2>
          <button onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>
        <div style={styles.modalBody}>
          {loading ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Loading impact summary...</div>
          ) : (
            <>
              {error && <div style={styles.error}>{error}</div>}
              <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: 10, padding: 16, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b', marginBottom: 12 }}>This action will:</div>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#94a3b8', fontSize: 13, lineHeight: 1.8 }}>
                  <li>Permanently delete <strong style={{ color: '#f1f5f9' }}>{info.length} organization{info.length > 1 ? 's' : ''}</strong></li>
                  <li>Remove <strong style={{ color: '#f1f5f9' }}>{totalUsers} user membership{totalUsers !== 1 ? 's' : ''}</strong> (user accounts will remain and show under Unassigned)</li>
                  <li>Unassign <strong style={{ color: '#f1f5f9' }}>{totalProjects} project{totalProjects !== 1 ? 's' : ''}</strong> (projects will not be deleted)</li>
                </ul>
              </div>
              <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: 10, maxHeight: 200, overflowY: 'auto' }}>
                {info.map((c) => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #2a2a2a', fontSize: 13 }}>
                    <span style={{ color: '#f1f5f9', fontWeight: 500 }}>{c.name}</span>
                    <span style={{ color: '#64748b', fontSize: 12 }}>{c.userCount} user{c.userCount !== 1 ? 's' : ''}, {c.projectCount} project{c.projectCount !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16 }}>
                <label style={{ ...styles.label, marginBottom: 8, display: 'block' }}>
                  Type <strong style={{ color: '#ef4444', fontFamily: 'monospace' }}>{confirmRequired}</strong> to confirm
                </label>
                <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} style={styles.input} placeholder={confirmRequired} autoFocus />
              </div>
              <div style={{ ...styles.modalFooter, ...(isMobile ? { flexDirection: 'column-reverse' as const } : {}) }}>
                <button onClick={onClose} style={{ ...styles.cancelBtn, ...(isMobile ? { minHeight: 44 } : {}) }}>Cancel</button>
                <button
                  onClick={handleDelete}
                  disabled={!canDelete || deleting}
                  style={{
                    background: canDelete ? 'rgba(239, 68, 68, 0.85)' : 'rgba(239, 68, 68, 0.2)',
                    color: canDelete ? '#fff' : '#666',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600,
                    cursor: canDelete ? 'pointer' : 'not-allowed',
                    ...(isMobile ? { minHeight: 44 } : {}),
                  }}
                >
                  {deleting ? 'Deleting...' : `Delete ${info.length} Client${info.length > 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateClientModal({ onClose, onCreated, isMobile }: { onClose: () => void; onCreated: () => void; isMobile: boolean }) {
  const [form, setForm] = useState({ name: '', contactEmail: '', contactName: '', notes: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Organization name is required.'); return; }
    setSaving(true);
    try {
      await api.admin.createClient({
        name: form.name,
        slug: slugify(form.name),
        contactEmail: form.contactEmail || null,
        contactName: form.contactName || null,
        notes: form.notes || null,
      });
      onCreated();
    } catch (err: any) {
      setError(err.message || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>New Client</h2>
          <button onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} style={styles.modalBody}>
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.field}>
            <label style={styles.label}>Organization Name *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={styles.input} placeholder="Acme Corp" required autoFocus />
          </div>
          <div style={{ ...styles.fieldRow, ...(isMobile ? { flexDirection: 'column' as const, gap: 20 } : {}) }}>
            <div style={styles.field}>
              <label style={styles.label}>Contact Name</label>
              <input type="text" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} style={styles.input} placeholder="John Doe" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Contact Email</label>
              <input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} style={styles.input} placeholder="john@acme.com" />
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...styles.input, minHeight: 80, resize: 'vertical' }} placeholder="Optional notes about this client..." />
          </div>
          <div style={{ ...styles.modalFooter, ...(isMobile ? { flexDirection: 'column-reverse' as const } : {}) }}>
            <button type="button" onClick={onClose} style={{ ...styles.cancelBtn, ...(isMobile ? { minHeight: 44 } : {}) }}>Cancel</button>
            <button type="submit" style={{ ...styles.submitBtn, ...(isMobile ? { minHeight: 44 } : {}) }} disabled={saving}>
              {saving ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  heading: { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  newBtn: { background: '#4F7CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  secondaryBtn: { background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  deleteBtn: { background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  smallBtn: { background: '#333333', color: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  approveBtn: { background: '#166534', color: '#86efac', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  tabBar: { display: 'flex', gap: 4, background: '#1a1a1a', border: '1px solid #333333', borderRadius: 10, padding: 4 },
  tabBtn: { background: 'none', border: 'none', color: '#94a3b8', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' },
  tabBtnActive: { background: '#333333', color: '#f1f5f9' },
  tabCount: { background: 'rgba(255,255,255,0.06)', color: '#94a3b8', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 },
  search: { background: '#1a1a1a', border: '1px solid #333333', borderRadius: 8, padding: '10px 14px', color: '#f1f5f9', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  select: { background: '#0d0d0d', border: '1px solid #333333', borderRadius: 6, padding: '6px 10px', color: '#f1f5f9', fontSize: 13, outline: 'none' },
  hint: { color: '#64748b', fontSize: 13, margin: '0 0 12px', lineHeight: 1.5 },
  errorBanner: { background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  noticeBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,204,136,0.1)', border: '1px solid rgba(0,204,136,0.4)', color: '#00cc88', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 },
  empty: { padding: 20, color: '#64748b', textAlign: 'center', fontSize: 14 },
  checkbox: { width: 16, height: 16, cursor: 'pointer', accentColor: '#4F7CFF' },
  table: { background: '#1a1a1a', border: '1px solid #333333', borderRadius: 12, overflow: 'hidden' },
  tableHeader: { display: 'flex', padding: '10px 20px', borderBottom: '1px solid #333333', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  row: { display: 'flex', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid #1a1a1a', textDecoration: 'none', color: 'inherit', fontSize: 14 },
  logoThumb: { width: 32, height: 32, borderRadius: 6, background: '#0d0d0d', border: '1px solid #333333', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  logoThumbImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  enabledBadge: { background: '#052e16', color: '#86efac', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  disabledBadge: { background: '#1e293b', color: '#64748b', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#1a1a1a', border: '1px solid #333333', borderRadius: 16, width: 480, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #333333' },
  modalTitle: { fontSize: 18, fontWeight: 600, color: '#f1f5f9', margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: 24, cursor: 'pointer', padding: '0 4px', lineHeight: 1 },
  modalBody: { padding: 24, display: 'flex', flexDirection: 'column', gap: 20 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1 },
  fieldRow: { display: 'flex', gap: 16 },
  label: { color: '#94a3b8', fontSize: 12, fontWeight: 500 },
  input: { background: '#0d0d0d', border: '1px solid #333333', borderRadius: 8, padding: '10px 14px', color: '#f1f5f9', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  error: { background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  cancelBtn: { background: 'none', border: '1px solid #475569', borderRadius: 8, color: '#94a3b8', padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  submitBtn: { background: '#4F7CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
